/** Kachel „Objekte": wer war zuletzt wo – offline aus dem Cache, eigene Outbox eingerechnet. */
import { cachedGet, currentMe, outbox } from '../store.js';
import { dayLabel, esc } from '../ui.js';
import { berlinTime } from '../../shared/time.js';
import {
  entryToVisit, overlayVisits, relativeDays, summarizeVisits, visitStatus, type VisitObject, type VisitRecord, type VisitStatus,
} from '../../shared/visits.js';

interface VisitsResponse {
  objects: VisitObject[];
  visits: VisitRecord[];
  serverTime: number;
}

export async function loadVisits(now = Date.now()): Promise<{ status: VisitStatus[]; offline: boolean; stand: number | null }> {
  let data: VisitsResponse | null = null;
  let offline = true;
  try {
    const r = await cachedGet<VisitsResponse>('visits', '/visits');
    data = r.data;
    offline = r.offline;
  } catch {
    /* Cache leer */
  }
  if (!data) return { status: [], offline, stand: null };
  const me = currentMe();
  const local = me
    ? (await outbox.list()).map((i) => ({
        id: i.id,
        visit: entryToVisit({ ...i.entry, userId: me.user.id }, me.user.name, data!.objects, now),
      }))
    : [];
  const summary = summarizeVisits(overlayVisits(data.visits, local));
  return { status: data.objects.map((o) => visitStatus(o, summary.get(o.key), now)), offline, stand: data.serverTime };
}

const AMPEL_TEXT = { gruen: 'im Plan', gelb: 'bald fällig', rot: 'überfällig' } as const;

function tile(s: VisitStatus): string {
  const lines: string[] = [];
  if (s.present.length) {
    lines.push(`<span class="visit-now">gerade vor Ort: ${s.present
      .map((p) => `${esc(p.userName)}${p.time ? ` (seit ${esc(p.time)})` : ''}`)
      .join(', ')}</span>`);
  } else if (s.last) {
    lines.push(`<span>zuletzt ${esc(s.last.userName)}, ${esc(dayLabel(s.last.day))}${s.last.time ? ` ${esc(s.last.time)}` : ''} (${relativeDays(s.daysSince!)})</span>`);
  } else {
    lines.push('<span class="muted">noch kein Besuch erfasst</span>');
  }
  if (s.ampel && s.dueDate && s.daysUntilDue !== null && !s.present.length) {
    const due = s.daysUntilDue < 0
      ? `überfällig seit ${esc(dayLabel(s.dueDate))}`
      : s.daysUntilDue === 0 ? 'heute fällig' : s.daysUntilDue === 1 ? 'morgen fällig' : `fällig bis ${esc(dayLabel(s.dueDate))}`;
    lines.push(`<span class="small">${due}</span>`);
  }
  if (s.recentHint) lines.push(`<span class="visit-hint">${esc(s.recentHint)}</span>`);
  return `<div class="visit ${s.ampel ? `amp-${s.ampel}` : 'amp-none'}">
    <div class="visit-head"><strong>${esc(s.label)}</strong>${s.ampel ? `<span class="amp-label">${AMPEL_TEXT[s.ampel]}</span>` : ''}</div>
    ${lines.join('')}</div>`;
}

export function visitsHtml(d: { status: VisitStatus[]; offline: boolean; stand: number | null }): string {
  if (!d.status.length) return '';
  return `<section class="visits"><div class="section-head"><h2>Objekte</h2>
    ${d.offline && d.stand ? `<span class="muted small">offline · Stand ${esc(berlinTime(d.stand))}</span>` : ''}</div>
    <div class="visit-grid">${d.status.map(tile).join('')}</div></section>`;
}
